import { CaretUpOutlined } from '@ant-design/icons';
import { Button, Checkbox } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { FilterOptionType } from './types';
import { Entity, EntityType, Tag } from '../../../types.generated';
import { generateColor } from '../../entity/shared/components/styled/StyledTag';
import { ANTD_GRAY } from '../../entity/shared/constants';
import { useEntityRegistry } from '../../useEntityRegistry';
import {
    ENTITY_SUB_TYPE_FILTER_NAME,
    ENTITY_FILTER_NAME,
    MAX_COUNT_VAL,
    PLATFORM_FILTER_NAME,
    TAGS_FILTER_NAME,
    TYPE_NAMES_FILTER_NAME,
} from '../utils/constants';
import { IconSpacer, Label } from './ActiveFilter';
import {
    isFilterOptionSelected,
    getFilterIconAndLabel,
    isAnyOptionSelected,
    getParentEntities,
    manageFilterDropdownAccessibility,
} from './utils';
import { capitalizeFirstLetterOnly } from '../../shared/textUtil';
import ParentEntities from './ParentEntities';
import { formatNumber } from '../../shared/formatNumber';

const FilterOptionWrapper = styled.div<{ centerAlign?: boolean; addPadding?: boolean }>`
    display: flex;
    align-items: center;

    label {
        padding: 5px 12px;
        width: 100%;
        height: 100%;
        ${(props) =>
            props.centerAlign &&
            `
            display: flex;
            align-items: center;
        `};

        .ant-checkbox + span {
            flex-grow: 1;
        }
    }

    ${(props) => props.addPadding && 'padding-left: 16px;'}

    &:hover {
        background-color: ${ANTD_GRAY[3]};
    }
`;

const StyledCheckbox = styled(Checkbox)`
    font-size: 14px;
    .ant-checkbox-inner {
        border-color: ${ANTD_GRAY[7]};
    }
    .ant-checkbox-checked {
        .ant-checkbox-inner {
            border-color: ${(props) => props.theme.styles['primary-color']};
        }
    }
`;

const TagColor = styled.span<{ color: string; colorHash?: string | null }>`
    height: 8px;
    width: 8px;
    border-radius: 50%;
    background-color: ${(props) => (props.color ? props.color : generateColor.hex(props.colorHash))};
    margin-right: 3px;
`;

const CountText = styled.span`
    height: 28px;
    color: #1ca8dd;
    min-width: 24px;
    font-size: 14px;
    padding: 0px 4px;
    margin-left: 6px;
    border-radius: 4px;
    align-items: center;
    display: inline-flex;
    justify-content: center;
    background-color: #f3fafd;
    border: 1px solid #1ca8dd;
`;

const LabelCountWrapper = styled.div`
    margin-left: auto;
`;

const ArrowButton = styled(Button)<{ isOpen: boolean }>`
    height: 24px;
    margin-left: 4px;
    width: 32px !important;
    background-color: transparent;

    > span {
        width: 100%;
    }

    svg {
        height: 12px;
        width: 12px;
        transition: transform 0.2s ease-in-out;

        ${(props) =>
            props.isOpen &&
            `
            transform: rotate(180deg);
        `}
    }

    &:hover,
    &:focus,
    &:active {
        background-color: transparent;
    }
`;

const ParentWrapper = styled.div`
    max-width: 220px;
`;

interface Props {
    showCount: boolean;
    filterOption: FilterOptionType;
    selectedFilterOptions: FilterOptionType[];
    setSelectedFilterOptions: (filterValues: FilterOptionType[]) => void;
    nestedOptions?: FilterOptionType[];
    addPadding?: boolean;
}

export default function FilterOption({
    showCount,
    filterOption,
    selectedFilterOptions,
    setSelectedFilterOptions,
    nestedOptions,
    addPadding,
}: Props) {
    const [areChildrenVisible, setAreChildrenVisible] = useState(true);
    const { field, value, count, entity } = filterOption;

    const entityRegistry: any = useEntityRegistry();
    const { icon, label } = getFilterIconAndLabel(field, value, entityRegistry, entity || null, 14);
    const shouldShowIcon = field === PLATFORM_FILTER_NAME && icon !== null;
    const shouldShowTagColor = field === TAGS_FILTER_NAME && entity?.type === EntityType.Tag;
    const isSubTypeFilter = field === TYPE_NAMES_FILTER_NAME;
    const parentEntities: Entity[] = getParentEntities(entity as Entity) || [];
    // only entity type filters return 10,000 max aggs
    const countText = count === MAX_COUNT_VAL && field === ENTITY_SUB_TYPE_FILTER_NAME ? '10k+' : formatNumber(count);

    function updateFilterValues() {
        if (isFilterOptionSelected(selectedFilterOptions, value)) {
            setSelectedFilterOptions(selectedFilterOptions.filter((option) => option.value !== value));
        } else {
            // if selecting parent filter, remove nested filter values
            const filteredSelectedOptions = selectedFilterOptions.filter(
                (o) => !nestedOptions?.some((nestedOption) => nestedOption.value === o.value),
            );
            setSelectedFilterOptions([...filteredSelectedOptions, filterOption]);
        }
    }

    const fieldId = field === ENTITY_SUB_TYPE_FILTER_NAME ? ENTITY_FILTER_NAME : field;

    // handle accessibility for keyboard navigation
    const handleKeyDown = (event: React.KeyboardEvent) => {
        manageFilterDropdownAccessibility(event, { field: fieldId, updateFilterValues });
    };

    return (
        <>
            <FilterOptionWrapper centerAlign={parentEntities.length > 0} addPadding={addPadding}>
                <StyledCheckbox
                    checked={isFilterOptionSelected(selectedFilterOptions, value)}
                    // show indeterminate if a nested option is selected
                    indeterminate={isAnyOptionSelected(
                        selectedFilterOptions,
                        nestedOptions?.map((o) => o.value),
                    )}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    id={`filter-option-${fieldId}`} // for keyboard navigation
                    aria-label={label}
                    onClick={updateFilterValues}
                    data-testid={`filter-option-${label}`}
                >
                    {parentEntities.length > 0 && (
                        <ParentWrapper>
                            <ParentEntities parentEntities={parentEntities} showEndingSeparator />
                        </ParentWrapper>
                    )}
                    {shouldShowIcon && <>{icon}</>}
                    {shouldShowTagColor && (
                        <TagColor color={(entity as Tag).properties?.colorHex || ''} colorHash={entity?.urn} />
                    )}
                    {(shouldShowIcon || shouldShowTagColor) && <IconSpacer />}
                    <Label ellipsis={{ tooltip: label }} style={{ maxWidth: 150 }}>
                        {isSubTypeFilter ? capitalizeFirstLetterOnly(label as string) : label}
                    </Label>

                    <LabelCountWrapper>
                        {showCount && <CountText className="f-pri-tag-outline ml-2">{countText}</CountText>}
                    </LabelCountWrapper>
                    {nestedOptions && nestedOptions.length > 0 && (
                        <ArrowButton
                            type="text"
                            className="icon-button"
                            icon={<CaretUpOutlined />}
                            onClick={() => setAreChildrenVisible(!areChildrenVisible)}
                            isOpen={areChildrenVisible}
                        />
                    )}
                </StyledCheckbox>
            </FilterOptionWrapper>
            {areChildrenVisible && (
                <>
                    {nestedOptions?.map((option) => (
                        <FilterOption
                            key={option.value}
                            showCount={showCount}
                            filterOption={option}
                            selectedFilterOptions={selectedFilterOptions}
                            setSelectedFilterOptions={setSelectedFilterOptions}
                            addPadding
                        />
                    ))}
                </>
            )}
        </>
    );
}
